import { useEffect, useState } from 'react';
import { useSEO } from '../../utils/seo';
import { useNavigate } from 'react-router-dom';
import Footer from '../home/components/Footer';

export default function TermsPage() {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://boostio.fi';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Käyttöehdot - Boostio',
    description: 'Boostio-palvelun käyttöehdot ja tietosuojaseloste.',
    url: `${siteUrl}/terms`,
    publisher: {
      '@type': 'Organization',
      name: 'Boostio',
      url: siteUrl
    }
  };

  useSEO({
    title: 'Käyttöehdot ja tietosuojaseloste - Boostio',
    description: 'Boostio-palvelun käyttöehdot, tietosuojaseloste ja evästekäytännöt. Lue, miten käsittelemme tietojasi ja mitä oikeuksia sinulla on.',
    keywords: 'käyttöehdot, tietosuojaseloste, evästeet, GDPR, tietosuoja',
    canonical: `${siteUrl}/terms`,
    schema
  });

  useEffect(() => {
    // Handle anchor links
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button onClick={scrollToTop} className="flex items-center cursor-pointer">
              <img 
                src="https://static.readdy.ai/image/a7fa12b8ba3e9f29132c64cff29b528e/52f4eaf466cfed1c522eca41976a3427.png" 
                alt="Boostio" 
                className="h-10 brightness-0"
              />
            </button>

            <div className="hidden md:flex items-center gap-8">
              <button onClick={goToHome} className="text-gray-700 hover:text-[#1E3A8A] font-medium transition-colors cursor-pointer">
                Etusivu
              </button>
            </div>

            <div className="hidden md:block">
              <button onClick={goToHome} className="bg-[#1E3A8A] text-white px-6 py-2.5 rounded-full hover:bg-[#1E40AF] transition-all cursor-pointer whitespace-nowrap font-semibold">
                Aloita ilmainen kokeilu
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-gray-700"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {isMobileMenuOpen && (
            <div className="md:hidden mt-4 py-4 rounded-2xl bg-white shadow-lg">
              <div className="flex flex-col space-y-1">
                <button 
                  onClick={goToHome} 
                  className="text-gray-700 hover:bg-gray-50 px-6 py-3 text-left font-medium transition-colors cursor-pointer"
                >
                  Etusivu
                </button>
                <div className="px-6 pt-2">
                  <button 
                    onClick={goToHome} 
                    className="w-full bg-[#1E3A8A] text-white px-6 py-3 rounded-full hover:bg-[#1E40AF] transition-all cursor-pointer whitespace-nowrap font-semibold"
                  >
                    Aloita ilmainen kokeilu
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>
      <main className="max-w-4xl mx-auto px-6 py-24">
        {/* Content */}
        <div className="pt-24 pb-20">
          <div className="max-w-4xl mx-auto px-6">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">Käyttöehdot & Tietosuoja</h1>
            
            <div className="prose prose-lg max-w-none">
              {/* Käyttöehdot */}
              <section className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">Käyttöehdot</h2>
                
                <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">1. Palvelun käyttö</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Boostio-palvelua käyttämällä hyväksyt nämä käyttöehdot. Palvelu on tarkoitettu yrityskäyttöön Google-arvostelujen keräämiseen ja hallintaan. Sitoudut käyttämään palvelua lain ja hyvien tapojen mukaisesti.
                </p>

                <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">2. Käyttäjätili</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Palvelun käyttö edellyttää rekisteröitymistä ja käyttäjätilin luomista. Olet vastuussa käyttäjätilisi tietojen oikeellisuudesta ja salasanan turvallisuudesta. Ilmoita meille välittömästi, jos epäilet tiliisi kohdistunutta luvattomaa käyttöä.
                </p>

                <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">3. Maksut ja laskutus</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Palvelu on maksullinen valitsemasi tilausmallin mukaan. Laskutus tapahtuu kuukausittain tai vuosittain. Hinnat sisältävät arvonlisäveron. Pidätämme oikeuden muuttaa hintoja ilmoittamalla siitä etukäteen.
                </p>

                <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">4. Tilauksen peruuttaminen</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Voit peruuttaa tilauksesi milloin tahansa. Peruutus astuu voimaan kuluvan laskutuskauden päättyessä. Maksetut maksut eivät ole palautettavissa, ellei laissa toisin säädetä.
                </p>

                <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">5. Immateriaalioikeudet</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Kaikki palveluun liittyvät immateriaalioikeudet kuuluvat Boostiolle. Saat rajoitetun, ei-yksinomaisen oikeuden käyttää palvelua näiden ehtojen mukaisesti. Et saa kopioida, muokata tai levittää palvelua ilman lupaa.
                </p>

                <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">6. Vastuunrajoitukset</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Palvelu tarjotaan "sellaisena kuin se on". Emme takaa palvelun keskeytymätöntä toimintaa. Emme ole vastuussa välillisistä vahingoista, jotka aiheutuvat palvelun käytöstä tai käyttämättömyydestä.
                </p>

                <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">7. Muutokset ehtoihin</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Pidätämme oikeuden muuttaa näitä ehtoja. Ilmoitamme merkittävistä muutoksista sähköpostitse tai palvelussa. Jatkamalla palvelun käyttöä muutosten jälkeen hyväksyt uudet ehdot.
                </p>
              </section>

              {/* Tietosuojaseloste */}
              <section className="mb-16 border-t-2 border-gray-200 pt-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Tietosuojaseloste</h2>
                
                <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">1. Rekisterinpitäjä</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Toiminimi Armas Muukka<br />
                  Y-tunnus: 3442167-1<br />
                  Osoite: Tarjantie 17, 15900 Lahti<br />
                  Sähköposti: info@boostio.fi
                </p>

                <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">2. Kerättävät tiedot</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Keräämme seuraavia tietoja:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Yhteystiedot (nimi, sähköposti, puhelinnumero)</li>
                  <li>Yritystiedot (yrityksen nimi, Y-tunnus)</li>
                  <li>Käyttötiedot (kirjautumisajat, käytetyt toiminnot)</li>
                  <li>Asiakastiedot (asiakkaiden sähköpostiosoitteet arvostelukyselyitä varten)</li>
                  <li>Laskutustiedot</li>
                </ul>

                <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">3. Tietojen käyttötarkoitus</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Käytämme tietoja seuraaviin tarkoituksiin:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Palvelun tarjoaminen ja ylläpito</li>
                  <li>Asiakaspalvelu ja tuki</li>
                  <li>Laskutus ja maksujen käsittely</li>
                  <li>Palvelun kehittäminen</li>
                  <li>Lakisääteiset velvoitteet</li>
                </ul>

                <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">4. Tietojen luovuttaminen</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Emme luovuta tietojasi kolmansille osapuolille ilman suostumustasi, paitsi:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Palvelun toimittamiseen tarvittaville alihankkijoille (esim. sähköpostipalvelu)</li>
                  <li>Lakisääteisten velvoitteiden täyttämiseksi</li>
                  <li>Oikeuksiemme suojaamiseksi</li>
                </ul>

                <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">5. Tietojen säilytysaika</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Säilytämme henkilötietojasi niin kauan kuin se on tarpeen palvelun tarjoamiseksi tai lakisääteisten velvoitteiden täyttämiseksi. Asiakastiedot poistetaan 12 kuukauden kuluttua tilauksen päättymisestä.
                </p>

                <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">6. Tietoturvakäytännöt</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Suojaamme tietosi asianmukaisin teknisin ja organisatorisin toimenpitein. Käytämme SSL-salausta tiedonsiirroissa ja tiedot tallennetaan suojatuille palvelimille. Pääsy tietoihin on rajoitettu vain valtuutetuille henkilöille.
                </p>

                <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">7. Rekisteröidyn oikeudet</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Sinulla on oikeus:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Tarkastaa sinusta tallennetut tiedot</li>
                  <li>Oikaista virheelliset tiedot</li>
                  <li>Poistaa tietosi ("oikeus tulla unohdetuksi")</li>
                  <li>Rajoittaa tietojen käsittelyä</li>
                  <li>Vastustaa tietojen käsittelyä</li>
                  <li>Siirtää tietosi toiselle palveluntarjoajalle</li>
                  <li>Tehdä valitus valvontaviranomaiselle</li>
                </ul>

                <h3 id="cookies" className="text-2xl font-semibold text-gray-800 mb-4 mt-8">8. Evästeet (Cookies)</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Käytämme evästeitä parantaaksemme käyttökokemusta ja analysoidaksemme palvelun käyttöä.
                </p>
                
                <h4 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Mitä evästeet ovat?</h4>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Evästeet ovat pieniä tekstitiedostoja, jotka tallennetaan laitteellesi, kun vierailet verkkosivustollamme. Ne auttavat meitä tunnistamaan sinut uudelleen vieraillessasi sivustollamme ja muistamaan mieltymyksesi.
                </p>

                <h4 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Käyttämämme evästeet:</h4>
                
                <div className="bg-gray-50 rounded-lg p-5 mb-4">
                  <h5 className="font-semibold text-gray-800 mb-2">Välttämättömät evästeet</h5>
                  <p className="text-gray-700 text-sm leading-relaxed mb-2">
                    Nämä evästeet ovat välttämättömiä sivuston perustoimintojen kannalta. Ne mahdollistavat esimerkiksi kirjautumisen ja turvallisen selaamisen.
                  </p>
                  <ul className="list-disc pl-5 text-gray-700 text-sm space-y-1">
                    <li><strong>cookieConsent</strong> - Tallentaa evästeasetuksesi (voimassa: 1 vuosi)</li>
                    <li><strong>session</strong> - Ylläpitää istuntoasi palvelussa (voimassa: istunnon ajan)</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-5 mb-4">
                  <h5 className="font-semibold text-gray-800 mb-2">Analytiikkaevästeet</h5>
                  <p className="text-gray-700 text-sm leading-relaxed mb-2">
                    Nämä evästeet auttavat meitä ymmärtämään, miten käytät sivustoamme. Keräämme anonyymia tietoa sivuston käytöstä parantaaksemme palvelua.
                  </p>
                  <ul className="list-disc pl-5 text-gray-700 text-sm space-y-1">
                    <li><strong>_ga</strong> - Google Analytics -tunniste (voimassa: 2 vuotta)</li>
                    <li><strong>_gid</strong> - Google Analytics -istuntotunniste (voimassa: 24 tuntia)</li>
                  </ul>
                </div>

                <h4 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Evästeiden hallinta</h4>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Voit hallita evästeasetuksia selaimesi kautta. Huomaa, että evästeiden estäminen voi vaikuttaa palvelun toimivuuteen. Voit myös muuttaa evästeasetuksiasi milloin tahansa poistamalla <strong>cookieConsent</strong>-evästeen selaimestasi, jolloin evästebanneri näytetään uudelleen.
                </p>

                <p className="text-gray-700 leading-relaxed mb-4">
                  Ohjeet evästeiden hallintaan eri selaimissa:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] hover:underline">Google Chrome</a></li>
                  <li><a href="https://support.mozilla.org/fi/kb/evasteet" target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] hover:underline">Mozilla Firefox</a></li>
                  <li><a href="https://support.apple.com/fi-fi/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] hover:underline">Safari</a></li>
                  <li><a href="https://support.microsoft.com/fi-fi/microsoft-edge/evasteiden-poistaminen-microsoft-edgessa-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] hover:underline">Microsoft Edge</a></li>
                </ul>

                <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">9. Yhteydenotot</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Jos sinulla on kysymyksiä tietosuojasta tai haluat käyttää oikeuksiasi, ota yhteyttä:
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Sähköposti: info@boostio.fi
                </p>

                <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">10. Muutokset tietosuojaselosteeseen</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Pidätämme oikeuden päivittää tätä tietosuojaselostetta. Ilmoitamme merkittävistä muutoksista palvelussa tai sähköpostitse. Suosittelemme tarkistamaan tietosuojaselosteen säännöllisesti.
                </p>

                <p className="text-gray-600 text-sm mt-8 italic">
                  Viimeksi päivitetty: {new Date().toLocaleDateString('fi-FI')}
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
